import { useState } from "react";
import {
  Accordion,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Heading,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Link } from "@tanstack/react-router";
import { LuArrowUpRight } from "react-icons/lu";
import { useQuery } from "@/lib/convex";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { ListSkeleton } from "../components/LoadingSkeletons";
import { PageHeader } from "../components/PageHeader";
import { ProgressRows, type RowHighlight } from "../components/ProgressRows";
import { useEffectivePersonId } from "../impersonation";
import { DelegatesCard } from "./DelegatesCard";

/**
 * The students somebody supervises, and where they are the holdup.
 *
 * "Waiting on you" is the reason this page exists. A student can be stuck on
 * a step their supervisor performs, and until the actor was separated from
 * the reviewer there was no way to compute that. The review queue answers it
 * across everybody at once; this attributes it per student, which is what
 * turns "three things pending" into "Ariadne is stuck on you".
 *
 * Every supervised student is listed, not only those with something
 * outstanding: "everything is fine" is information too.
 *
 * The same page serves co-supervisors: the students they co-supervise, with
 * the same rows and the same "waiting on you".
 */
const VARIANTS = {
  group: {
    title: "My group",
    description: "Everybody you supervise, and what each of them is waiting on.",
    empty: "You do not supervise anybody in CeMMbase. Group heads, their deputies and delegates see their group’s students here.",
    from: "group",
  },
  coSupervision: {
    title: "Co-supervision",
    description: "The students you co-supervise, and what each of them is waiting on.",
    empty: "You do not co-supervise anybody in CeMMbase. A student’s supervisor or an activity admin records their co-supervisor.",
    from: "coSupervision",
  },
} as const;

export function SupervisedStudentsPage({ variant = "group" }: { variant?: keyof typeof VARIANTS }) {
  const config = VARIANTS[variant];
  // Which count is being pointed at, and on whose row. Keyed by person so
  // two expanded students cannot light each other's rows.
  const [hovered, setHovered] = useState<{ personId: string; what: RowHighlight } | null>(null);
  const effectivePersonId = useEffectivePersonId();
  const args = { effectivePersonId: effectivePersonId as Id<"persons"> | null };
  // Both queries are declared and one is skipped: hooks cannot be chosen
  // conditionally, and the two return the same shape.
  const supervised = useQuery(api.programmeProgress.supervisedStudents, variant === "group" ? args : "skip");
  const coSupervised = useQuery(api.programmeProgress.coSupervisedStudents, variant === "coSupervision" ? args : "skip");
  const students = variant === "group" ? supervised : coSupervised;

  const breadcrumbs = [{ label: "CeMMbase", to: "/" }, { label: config.title }];
  const owed = (students ?? []).reduce((sum, student) => sum + student.waitingOnYou, 0);

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={config.title}
        description={config.description}
        breadcrumbs={breadcrumbs}
        actions={
          owed > 0 ? (
            <Badge colorPalette="orange">
              {owed} waiting on you
            </Badge>
          ) : undefined
        }
      />

      {/* The full width, with the delegates beside the people on a wide
          screen: capped at a reading width, the list left half the screen
          empty, and the delegates card stretched one name and one button
          across all of it. */}
      <Flex
        direction={{ base: "column", xl: "row" }}
        align={{ xl: "flex-start" }}
        gap={6}
        px={{ base: 6, sm: 8, lg: 10 }}
        py={{ base: 6, lg: 8 }}
      >
        {/* The head's alone: it renders nothing for anybody else. Above the
            list on a narrow screen, as before; beside it on a wide one. */}
        {variant === "group" ? (
          <DelegatesCard w={{ base: "full", xl: "22rem" }} flexShrink={0} order={{ base: 0, xl: 1 }} />
        ) : null}
        <Stack gap={6} flex="1" minW={0}>
          {students === undefined ? (
            <ListSkeleton label="Loading" rows={4} />
          ) : students.length === 0 ? (
            <Text fontSize="sm" color="fg.muted">{config.empty}</Text>
          ) : (
            // On a card of their own, as every other list is: straight on the
            // page's grey they read as loose text rather than as a list.
            <Card.Root borderWidth="1px" borderColor="border">
              <Accordion.Root multiple variant="plain">
                {students.map((student, index) => (
                  <Accordion.Item
                    key={String(student.personId)}
                    value={String(student.personId)}
                    px={5}
                    borderTopWidth={index === 0 ? 0 : "1px"}
                    borderColor="border"
                  >
                    <Accordion.ItemTrigger py={3} cursor="pointer">
                      <HStack gap={3} flex="1" flexWrap="wrap">
                        <Avatar.Root size="sm">
                          <Avatar.Fallback name={student.personName} />
                          {student.profileImageUrl ? <Avatar.Image src={student.profileImageUrl} alt="" /> : null}
                        </Avatar.Root>
                        <Stack gap={0}>
                          <HStack gap={2}>
                            <Text fontSize="sm" fontWeight="medium">{student.personName}</Text>
                            <Badge variant="outline">{student.statusLabel}</Badge>
                            {student.isPaused ? (
                              <Badge colorPalette="purple" variant="subtle">Paused</Badge>
                            ) : null}
                          </HStack>
                          {/* What they are, then -- for students only -- which
                              intake and how far through it. Read outwards from the
                              person: the job holds for everybody, the cohort is
                              fixed once, and the year is the part that moves. */}
                          <Text fontSize="xs" color="fg.muted">
                            {[
                              student.positionLabel,
                              student.cohort === null ? null : `${student.cohort} cohort`,
                              student.year === null ? null : `Year ${student.year}`,
                            ].filter((part) => part !== null).join(" · ")}
                          </Text>
                        </Stack>

                        <Box flex="1" />

                        {/* The one number worth crossing a room for */}
                        {student.waitingOnYou > 0 ? (
                          <Badge
                            colorPalette="orange"
                            cursor="default"
                            onMouseEnter={() => setHovered({
                              personId: String(student.personId),
                              what: "waitingOnViewer",
                            })}
                            onMouseLeave={() => setHovered(null)}
                          >
                            {student.waitingOnYou} waiting on you
                          </Badge>
                        ) : null}
                        {student.overdue > 0 ? (
                          <Badge
                            colorPalette="orange"
                            variant="subtle"
                            cursor="default"
                            onMouseEnter={() => setHovered({ personId: String(student.personId), what: "late" })}
                            onMouseLeave={() => setHovered(null)}
                          >
                            {student.overdue} late
                          </Badge>
                        ) : null}
                        {student.blocked > 0 ? (
                          <Badge
                            colorPalette="gray"
                            variant="subtle"
                            cursor="default"
                            onMouseEnter={() => setHovered({ personId: String(student.personId), what: "blocked" })}
                            onMouseLeave={() => setHovered(null)}
                          >
                            {student.blocked} blocked
                          </Badge>
                        ) : null}
                        <Text fontSize="xs" color="fg.muted" fontVariantNumeric="tabular-nums">
                          {student.completed}/{student.total}
                        </Text>
                      </HStack>
                      <Accordion.ItemIndicator />
                    </Accordion.ItemTrigger>
                    <Accordion.ItemContent>
                      <Accordion.ItemBody pb={4}>
                        <ProgressRows
                          rows={student.rows}
                          origin={{ from: config.from }}
                          // All of the person's activities, not a year's.
                          emptyText={`Nothing is assigned to ${student.personName} yet.`}
                          highlight={hovered?.personId === String(student.personId)
                            ? hovered.what
                            : null}
                        />
                        {/* Everything they hold is listed above; the programme
                            record is the part that belongs to their programme,
                            laid out by year. Here rather than on the row above,
                            which is the accordion's own button -- a link inside
                            it would toggle the row as well as leave the page.
                            Only for the enrolled: anybody else's would be empty. */}
                        {student.year === null ? null : (
                          <Flex justify="flex-end" mt={2}>
                            <Button asChild variant="ghost" size="sm" colorPalette="blue">
                              <Link
                                to="/programme/$personId"
                                params={{ personId: String(student.personId) }}
                                search={{ from: config.from }}
                              >
                                Programme record
                                <LuArrowUpRight />
                              </Link>
                            </Button>
                          </Flex>
                        )}
                      </Accordion.ItemBody>
                    </Accordion.ItemContent>
                  </Accordion.Item>
                ))}
              </Accordion.Root>
            </Card.Root>
          )}

          {/* The key to the Paused badge, so only where one is shown. */}
          {students?.some((student) => student.isPaused) ? (
            <Text fontSize="xs" color="fg.muted">
              Paused: on leave, with no deadlines while they are away.
            </Text>
          ) : null}
        </Stack>
      </Flex>
    </Box>
  );
}
